import { useState } from "react";
import axios from "axios";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Compact consultation form used on doctor / specialty / treatment pages.
 * Reuses the /api/enquiries endpoint; injects context (doctor/treatment/specialty) into the message.
 */
export const ConsultationForm = ({
    context = "",
    treatmentSuggestion = "Other",
    title = "Book a Free Consultation",
    subtitle = "Share your case and a care coordinator will reach out within 24 hours.",
}) => {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        country: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone || !form.country) {
            toast.error("Please fill in all required fields.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                treatment: treatmentSuggestion,
                message: context
                    ? `[${context}] ${form.message || ""}`.trim()
                    : form.message || "",
            };
            await axios.post(`${API}/enquiries`, payload);
            setDone(true);
            toast.success("Enquiry received. We'll contact you within 24 hours.");
        } catch (err) {
            console.error(err);
            toast.error(
                err?.response?.data?.detail?.[0]?.msg ||
                    "Could not submit. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div
                data-testid="consultation-success"
                className="rounded-3xl bg-white border border-stone-200 shadow-xl p-8 text-center"
            >
                <div className="mx-auto h-14 w-14 rounded-full bg-brand-50 flex items-center justify-center mb-4">
                    <CheckCircle2 className="h-7 w-7 text-brand-700" />
                </div>
                <h3 className="font-display text-xl font-bold text-stone-900">
                    Thank you — your enquiry is in.
                </h3>
                <p className="mt-2 text-sm text-stone-600">
                    Our care coordinator will reach out within 24 hours.
                </p>
            </div>
        );
    }

    return (
        <div
            data-testid="consultation-form-wrapper"
            className="rounded-3xl bg-white border border-stone-200 shadow-xl p-6 md:p-8"
        >
            <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2">
                Free Consultation
            </div>
            <h3 className="font-display text-2xl font-bold text-stone-900">
                {title}
            </h3>
            <p className="mt-1 text-sm text-stone-600">{subtitle}</p>

            <form
                onSubmit={submit}
                data-testid="consultation-form"
                className="mt-6 space-y-4"
            >
                <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <Label htmlFor="cf-name">Full name *</Label>
                        <Input
                            id="cf-name"
                            data-testid="cf-name"
                            value={form.name}
                            onChange={(e) => update("name", e.target.value)}
                            placeholder="Your name"
                            className="h-11 rounded-xl"
                            required
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="cf-email">Email *</Label>
                        <Input
                            id="cf-email"
                            type="email"
                            data-testid="cf-email"
                            value={form.email}
                            onChange={(e) => update("email", e.target.value)}
                            placeholder="you@example.com"
                            className="h-11 rounded-xl"
                            required
                        />
                    </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <Label htmlFor="cf-phone">Phone / WhatsApp *</Label>
                        <Input
                            id="cf-phone"
                            data-testid="cf-phone"
                            value={form.phone}
                            onChange={(e) => update("phone", e.target.value)}
                            placeholder="+234 ..."
                            className="h-11 rounded-xl"
                            required
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="cf-country">Country *</Label>
                        <Input
                            id="cf-country"
                            data-testid="cf-country"
                            value={form.country}
                            onChange={(e) => update("country", e.target.value)}
                            placeholder="Your country"
                            className="h-11 rounded-xl"
                            required
                        />
                    </div>
                </div>
                <div className="space-y-1.5">
                    <Label htmlFor="cf-message">Tell us about your case</Label>
                    <Textarea
                        id="cf-message"
                        data-testid="cf-message"
                        value={form.message}
                        onChange={(e) => update("message", e.target.value)}
                        rows={3}
                        placeholder="Brief description, diagnosis or question..."
                        className="rounded-xl"
                    />
                </div>
                <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="cf-submit"
                    className="w-full rounded-full bg-orange-500 hover:bg-orange-600 text-white h-12 text-base shadow-lg"
                >
                    {submitting ? (
                        <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Submitting...
                        </>
                    ) : (
                        <>
                            <Send className="h-4 w-4 mr-2" />
                            Book Free Consultation
                        </>
                    )}
                </Button>
                <p className="text-xs text-stone-500">
                    We respect your privacy. Your details are used only to coordinate your care.
                </p>
            </form>
        </div>
    );
};
